import * as React from 'react';
import { Ref } from 'react';
import PropTypes from 'prop-types';
import { Identifier, MutationMode, RedirectionSideEffect } from 'react-admin';
import { MenuItemProps } from '@mui/material';
import { DeleteMenuItemWithConfirmation } from './DeleteMenuItemWithConfirmation';
import { DeleteMenuItemWithUndo } from './DeleteMenuItemWithUndo';
import { TreeRecord } from './types';

/**
 * MenuItem used to delete a branch. Added by default by the <NodeActions> of the TreeWithDetails component.
 *
 * @typedef {Object} Props The props you can use (other props are injected if you used it in the <NodeActions>)
 * @param {Prop} props
 * @prop {string} mutationMode Either 'undoable', 'optimistic' or 'pessimistic'. When using 'optimistic' or 'pessimistic', a confirmation dialog is displayed before the deletion. When using 'undoable', a notification is displayed and users have the possibility to undo the deletion.
 * @prop {string} className
 * @prop {string} label MenuItem label. Defaults to 'ra.action.delete, translated.
 *
 * @example
 * import { NodeActions, DeleteMenuItem, TreeWithDetails } from '@react-admin/ra-tree';
 *
 * const MyCustomActionMenuItem = ({ record, resource, parentId }) => {
 *     const handleClick = () => {
 *         // Do something with dataProvider ?
 *     }
 *     return (
 *         <MenuItem onClick={handleClick}>
 *             Do something
 *         </MenuItem>
 *     );
 * };
 *
 * const MyActions = (props) => (
 *     <NodeActions {...props}>
 *         <MyCustomActionMenuItem />
 *         <DeleteMenuItem />
 *     </NodeActions>
 * );
 *
 * const CategoriesList: FC<Props> = props => (
 *     <TreeWithDetails
 *         titleField="name"
 *         edit={CategoriesEdit}
 *         draggable
 *         showLine
 *         nodeActions={<MyActions />}
 *         {...props}
 *     />
 * );
 */
export const DeleteMenuItem = <RecordType extends TreeRecord = TreeRecord>(
    props: DeleteMenuItemProps<RecordType>
) => {
    const { mutationMode = 'undoable', ...rest } = props;

    return mutationMode === 'undoable' ? (
        <DeleteMenuItemWithUndo {...rest} mutationMode={mutationMode} />
    ) : (
        <DeleteMenuItemWithConfirmation {...rest} mutationMode={mutationMode} />
    );
};

DeleteMenuItem.displayName = 'DeleteMenuItem';

export interface DeleteMenuItemProps<RecordType extends TreeRecord = TreeRecord>
    extends Omit<MenuItemProps, 'id'> {
    id?: Identifier;
    redirect?: RedirectionSideEffect;
    ref?: Ref<HTMLLIElement>;
    resource?: string;
    record?: RecordType;
    mutationMode?: MutationMode;
}

DeleteMenuItem.propTypes = {
    className: PropTypes.string,
    confirmTitle: PropTypes.string,
    confirmContent: PropTypes.string,
    label: PropTypes.string,
    mutationMode: PropTypes.oneOf(['pessimistic', 'optimistic', 'undoable']),
    record: PropTypes.any,
    redirect: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
        PropTypes.func,
    ]),
    resource: PropTypes.string,
};
